import { useState, useMemo, useEffect } from "react";
import { HelpItem, HelpTypeFilter, HelpItemPayload } from "../types/HelpTypes";
import { HelpService, HelpItemSalesforce } from "../../../globals/services/help.service";

export const useHelpItems = () => {
  const [items, setItems] = useState<HelpItem[]>([]);
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<HelpTypeFilter>("all");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const helpService = HelpService();

  // Convert Salesforce item to local HelpItem format
  const convertFromSalesforce = (sfItem: HelpItemSalesforce): HelpItem => ({
    id: sfItem.Id || '',
    title: sfItem.Title__c,
    description: sfItem.Description__c || '',
    type: sfItem.Type__c,
    fileName: '', // Will be derived from URL if needed
    url: sfItem.URL__c || '',
    order: sfItem.Order__c || 0 // Map from Salesforce Order__c field
  });

  // Load help items from Salesforce
  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const sfItems = await helpService.getAll();
      const convertedItems = sfItems.map(convertFromSalesforce);
      
      // Sort by order field (items without order go to end)
      const sortedItems = convertedItems.sort((a, b) => {
        if (a.order === 0 && b.order === 0) return 0;
        if (a.order === 0) return 1;
        if (b.order === 0) return -1;
        return a.order - b.order;
      });
      
      setItems(sortedItems);
    } catch (err: any) {
      setError(err.message || 'Failed to load help items');
      console.error('Error loading help items:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load items on mount
  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(
    () =>
      items
        .filter(
          (i) =>
            i.title.toLowerCase().includes(search.toLowerCase()) ||
            (i.description || "").toLowerCase().includes(search.toLowerCase())
        )
        .filter((i) => (typeFilter === "all" ? true : i.type === typeFilter)),
    [items, search, typeFilter]
  );

  const addItem = async (payload: HelpItemPayload) => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate next order value (highest current order + 1)
      const maxOrder = items.reduce((max, item) => Math.max(max, item.order || 0), 0);
      const nextOrder = maxOrder + 1;
      
      const newSfItem = await helpService.create(
        payload.title,
        payload.description,
        payload.type,
        payload.url,
        payload.file,
        nextOrder // Pass the calculated order
      );
      
      const newItem = convertFromSalesforce(newSfItem);
      const updatedItems = [...items, newItem];
      
      // Sort the items to maintain proper order
      const sortedItems = updatedItems.sort((a, b) => {
        if (a.order === 0 && b.order === 0) return 0;
        if (a.order === 0) return 1;
        if (b.order === 0) return -1;
        return a.order - b.order;
      });
      
      setItems(sortedItems);
    } catch (err: any) {
      setError(err.message || 'Failed to create help item');
      console.error('Error adding help item:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (id: string, payload: HelpItemPayload) => {
    try {
      setLoading(true);
      setError(null);
      
      const updatedSfItem = await helpService.update(
        id,
        payload.title,
        payload.description,
        payload.type,
        payload.url,
        payload.file
      );
      
      const updatedItem = convertFromSalesforce(updatedSfItem);
      setItems(items.map(item => 
        item.id === id ? updatedItem : item
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to update help item');
      console.error('Error updating help item:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      await helpService.delete(id);
      setItems(items.filter(item => item.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete help item');
      console.error('Error deleting help item:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const reorderItems = async (reorderedItems: HelpItem[]) => {
    try {
      // Update local state immediately for better UX
      setItems(reorderedItems);
      
      // Prepare bulk update data for backend
      const bulkUpdateData = reorderedItems.map(item => ({
        id: item.id,
        order: item.order || 0
      }));
      
      // Send bulk order update to backend (no loading spinner)
      await helpService.bulkUpdateOrder(bulkUpdateData);
    } catch (err: any) {
      setError(err.message || 'Failed to update help items order');
      console.error('Error updating help items order:', err);
      // Reload items to revert to server state on error
      await loadItems();
    }
  };

  return {
    items,
    search,
    typeFilter,
    filteredItems,
    loading,
    error,
    setSearch,
    setTypeFilter,
    addItem,
    updateItem,
    deleteItem,
    reorderItems,
    refreshItems: loadItems,
  };
};
